import axios from 'axios';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { Button, message, Progress } from 'antd';
import Footer from '../component/Footer';
import { TrophyOutlined, ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import logo from '../component/image/logo.png';

export default function Competition() {
    const [cookies] = useCookies(['user']);
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [gameState, setGameState] = useState('start'); // start, playing, finished
    const [loading, setLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(15);
    const [ranking, setRanking] = useState([]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/competition/questions`, { params: { limit: 10 } });
            setQuestions(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRanking = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/competition/ranking`);
            setRanking(response?.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchRanking();
    }, []);

    useEffect(() => {
        let timer;
        if (gameState === 'playing' && timeLeft > 0 && selectedAnswer === null) {
            timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        } else if (timeLeft === 0 && selectedAnswer === null) {
            handleTimeout();
        }
        return () => clearTimeout(timer);
    }, [timeLeft, gameState, selectedAnswer]);

    const startGame = () => {
        if (!cookies?.user) {
            message.warning('Vui lòng đăng nhập để chơi!');
            navigate('/login');
            return;
        }
        fetchQuestions().then(() => {
            setGameState('playing');
            setCurrentIndex(0);
            setScore(0);
            setSelectedAnswer(null);
            setIsCorrect(null);
            setTimeLeft(15);
        });
    };

    const handleAnswer = (answer) => {
        if (selectedAnswer !== null) return;
        setSelectedAnswer(answer);
        const correct = questions[currentIndex].correct_answer === answer;
        setIsCorrect(correct);
        if (correct) {
            setScore(score + 1);
        }

        setTimeout(() => {
            if (currentIndex < questions.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setSelectedAnswer(null);
                setIsCorrect(null);
                setTimeLeft(15);
            } else {
                finishGame(correct ? score + 1 : score);
            }
        }, 1500);
    };

    const handleTimeout = () => {
        setSelectedAnswer('__timeout__');
        setIsCorrect(false);
        setTimeout(() => {
            if (currentIndex < questions.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setSelectedAnswer(null);
                setIsCorrect(null);
                setTimeLeft(15);
            } else {
                finishGame(score);
            }
        }, 1500);
    };

    const finishGame = async (finalScore) => {
        setGameState('finished');
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/competition/submit`, {
                user_id: cookies?.user?.id,
                score: finalScore,
                total_questions: questions.length
            });
            fetchRanking();
        } catch (error) {
            console.error(error);
        }
    };

    const currentQuestion = questions[currentIndex];

    return (
        <>
            <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e]">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#e94560] to-[#0f3460] text-white py-8">
                    <div className="max-w-screen-xl mx-auto px-6 text-center">
                        <h1 className="text-4xl md:text-5xl font-extrabold mb-2">⚡ Thi Đấu</h1>
                        <p className="text-lg text-pink-100">Thử thách kiến thức từ vựng tiếng Anh của bạn!</p>
                    </div>
                </div>

                <div className="max-w-screen-xl mx-auto px-6 py-8">
                    {gameState === 'start' && (
                        <div className="max-w-2xl mx-auto">
                            {/* Start Screen */}
                            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 md:p-12 text-center text-white mb-8">
                                <div className="text-6xl mb-6">🏆</div>
                                <h2 className="text-3xl font-bold mb-4">Sẵn sàng thi đấu?</h2>
                                <p className="text-gray-300 mb-2">• 10 câu hỏi từ vựng ngẫu nhiên</p>
                                <p className="text-gray-300 mb-2">• 15 giây cho mỗi câu</p>
                                <p className="text-gray-300 mb-6">• Chọn nghĩa đúng của từ tiếng Anh</p>
                                <Button
                                    onClick={startGame}
                                    loading={loading}
                                    className="!bg-gradient-to-r !from-[#e94560] !to-[#ff6b6b] !text-white !border-none !h-14 !px-12 !rounded-xl !text-lg !font-bold hover:!opacity-90"
                                    size="large"
                                >
                                    <ThunderboltOutlined /> Bắt đầu!
                                </Button>
                            </div>

                            {/* Ranking */}
                            {ranking.length > 0 && (
                                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 text-white">
                                    <h3 className="text-xl font-bold mb-4 text-center"><TrophyOutlined className="text-yellow-400" /> Bảng xếp hạng</h3>
                                    <div className="space-y-3">
                                        {ranking.slice(0, 10).map((r, index) => (
                                            <div key={index} className={`flex items-center gap-4 p-3 rounded-xl ${index < 3 ? 'bg-white bg-opacity-10' : ''}`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-white bg-opacity-20'}`}>
                                                    {index + 1}
                                                </div>
                                                <img src={r.avatar || logo} alt="" className="w-10 h-10 rounded-full object-cover" crossOrigin="anonymous" />
                                                <div className="flex-1">
                                                    <p className="font-semibold">{r.username}</p>
                                                    <p className="text-xs text-gray-400">{r.total_games} trận</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-yellow-400">{r.best_score} điểm</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {gameState === 'playing' && currentQuestion && (
                        <div className="max-w-2xl mx-auto">
                            {/* Progress */}
                            <div className="flex items-center justify-between mb-4 text-white">
                                <span className="font-semibold">Câu {currentIndex + 1}/{questions.length}</span>
                                <span className="font-semibold">Điểm: {score}</span>
                            </div>
                            <Progress percent={((currentIndex + 1) / questions.length) * 100} showInfo={false} strokeColor="#e94560" trailColor="rgba(255,255,255,0.1)" />

                            {/* Timer */}
                            <div className="flex justify-center my-6">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold border-4 
                                    ${timeLeft <= 5 ? 'border-red-500 text-red-400 animate-pulse' : 'border-blue-400 text-blue-300'}`}>
                                    {timeLeft}
                                </div>
                            </div>

                            {/* Question */}
                            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 text-center mb-6">
                                <p className="text-gray-400 text-sm mb-2">{currentQuestion.lesson_title}</p>
                                <h2 className="text-3xl md:text-4xl font-extrabold text-white">{currentQuestion.question}</h2>
                                <p className="text-gray-400 mt-2">Chọn nghĩa đúng:</p>
                            </div>

                            {/* Options */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {currentQuestion.options.map((option, index) => {
                                    let btnClass = 'bg-white bg-opacity-10 text-white hover:bg-opacity-20 border-2 border-transparent';
                                    if (selectedAnswer !== null) {
                                        if (option === currentQuestion.correct_answer) {
                                            btnClass = 'bg-green-500 bg-opacity-30 text-green-300 border-2 border-green-500';
                                        } else if (option === selectedAnswer && !isCorrect) {
                                            btnClass = 'bg-red-500 bg-opacity-30 text-red-300 border-2 border-red-500';
                                        }
                                    }
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => handleAnswer(option)}
                                            disabled={selectedAnswer !== null}
                                            className={`${btnClass} rounded-xl p-4 text-left transition-all font-medium backdrop-blur-sm`}
                                        >
                                            <span className="font-bold mr-2">{String.fromCharCode(65 + index)}.</span>
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Feedback */}
                            {selectedAnswer !== null && (
                                <div className={`mt-6 text-center text-xl font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                    {isCorrect ? '🎉 Chính xác!' : selectedAnswer === '__timeout__' ? '⏰ Hết giờ!' : '❌ Sai rồi!'}
                                </div>
                            )}
                        </div>
                    )}

                    {gameState === 'finished' && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 md:p-12 text-center text-white">
                                <div className="text-6xl mb-6">
                                    {score >= 8 ? '🏆' : score >= 5 ? '🎉' : '💪'}
                                </div>
                                <h2 className="text-3xl font-bold mb-4">Kết quả</h2>
                                <div className="text-6xl font-extrabold text-yellow-400 mb-2">{score}/{questions.length}</div>
                                <p className="text-gray-300 text-lg mb-8">
                                    {score >= 8 ? 'Xuất sắc! Bạn giỏi quá!' : score >= 5 ? 'Khá tốt! Hãy cố gắng hơn nữa!' : 'Hãy luyện tập thêm nhé!'}
                                </p>
                                <div className="flex gap-4 justify-center">
                                    <Button
                                        onClick={startGame}
                                        className="!bg-gradient-to-r !from-[#e94560] !to-[#ff6b6b] !text-white !border-none !h-12 !px-8 !rounded-xl !font-bold"
                                    >
                                        Chơi lại
                                    </Button>
                                    <Button
                                        onClick={() => setGameState('start')}
                                        className="!bg-white !bg-opacity-20 !text-white !border-none !h-12 !px-8 !rounded-xl !font-bold"
                                    >
                                        Xem BXH
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}
